import React, { useState, useEffect, useContext, useMemo, useCallback, lazy, Suspense } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faRedo } from '@fortawesome/free-solid-svg-icons';
import { MoviesAPI, ShowsAPI, UserAPI, PopularityAPI } from '../../api';
import { AppContext } from '../../context/AppContext';
import { AuthContext } from '../../context/AuthContext';
import ErrorNotification from '../Error/ErrorNotification';
import { Link } from 'react-router-dom';
import Featured from './Featured';

// Lazy load other components to reduce initial bundle size
const ContentSlider = lazy(() => import('./ContentSlider'));
const RecentlyAdded = lazy(() => import('./RecentlyAdded'));

// overlay
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  svg {
    font-size: 40px;
    color: var(--primary-light);
    animation: spin 1s linear infinite;
  }
  
  p {
    margin-top: 15px;
    color: white;
    font-size: 16px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SectionLoadingSpinner = styled(LoadingSpinner)`
  padding: 30px 0;
  
  svg {
    font-size: 30px;
  }
  
  p {
    font-size: 14px;
  }
`;

const HighTrafficMessage = styled.div`
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  margin-top: 15px;
  max-width: 350px;
  text-align: center;
  
  p {
    color: white;
    font-size: 14px;
    line-height: 1.4;
  }
`;

const ErrorOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 800px;
  width: 100%;
`;

// Fixed the SplitSection to manage heights and alignment properly
const SplitSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 20px;
  padding: 0 50px;
  margin-bottom: 40px;
  min-height: 250px; /* Ensure minimum height */
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr 300px;
    padding: 0 30px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 10px; /* Reduced padding to make content broader on mobile */
    gap: 20px;
    min-height: auto; /* Reset minimum height for mobile */
    margin-bottom: 30px;
  }
`;

const TrendingSection = styled.div`
  overflow: hidden;
  height: 100%; /* Ensure full height */
  
  @media (max-width: 768px) {
    padding: 0; /* Remove padding on mobile */
  }
`;

const ContentContainer = styled.div`
  /* Main container for all content sections */
  padding-bottom: 60px;
  
  @media (max-width: 768px) {
    padding-bottom: 40px;
    max-width: 100%;
    overflow-x: hidden;
  }
`;

const FeaturedContainer = styled.div`
  margin-bottom: 40px; /* Add space below the featured section */
`;

const RetryButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 20px auto 0;
  
  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-3px);
  }
  
  svg {
    margin-right: 10px;
  }
`;

const SuspenseFallback = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

// More efficient image preloader function with timeout to prevent hanging
const preloadImage = (src) => {
  if (!src) return Promise.resolve();
  
  return new Promise((resolve) => {
    const img = new Image();
    img.src = src;
    img.onload = resolve;
    img.onerror = resolve;
    // Set a timeout to resolve anyway after 2 seconds to prevent hanging
    setTimeout(resolve, 2000);
  });
};

const Footer = styled.footer`
  width: 100%;
  padding: 20px 0;
  margin-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 15px;
`;

const FooterLink = styled(Link)`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  transition: color 0.2s ease;
  
  &:hover {
    color: var(--primary-light);
  }
`;

const Disclaimer = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  line-height: 1.5;
  max-width: 600px;
  margin: 0 auto;
`;

const HomePage = () => {
  const { continueWatching, loading: userDataLoading, refreshUserData } = useContext(AppContext);
  const { currentUser } = useContext(AuthContext);
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [recentlyAdded, setRecentlyAdded] = useState([]);
  const [popularMovies, setPopularMovies] = useState([]);
  const [popularShows, setPopularShows] = useState([]);
  
  // Loading states for each section
  const [loading, setLoading] = useState(true);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [trendingLoading, setTrendingLoading] = useState(true);
  const [moviesLoading, setMoviesLoading] = useState(true);
  const [showsLoading, setShowsLoading] = useState(true);
  const [recentlyAddedLoading, setRecentlyAddedLoading] = useState(true);
  
  const [error, setError] = useState(null);
  const [dataRefreshed, setDataRefreshed] = useState(false);
  const [featuredLoaded, setFeaturedLoaded] = useState(false);
  const [showHighTrafficMessage, setShowHighTrafficMessage] = useState(false);
  
  // Show high traffic message if loading takes more than 3 seconds (reduced from 5)
  useEffect(() => {
    let timeoutId;
    
    if (loading) {
      timeoutId = setTimeout(() => {
        setShowHighTrafficMessage(true);
      }, 3000); // Reduced from 5000
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [loading]);
  
  // Optimize user data refresh - only once when needed
  useEffect(() => {
    if (currentUser && !userDataLoading && !dataRefreshed) {
      // Small delay to prevent immediate API call
      const timer = setTimeout(() => {
        refreshUserData();
        setDataRefreshed(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [currentUser, refreshUserData, userDataLoading, dataRefreshed]);
  
  // Reset the refresh flag when user changes
  useEffect(() => {
    setDataRefreshed(false);
  }, [currentUser]);
  
  // Memoized fetch function for featured content - prioritize this
  const fetchFeaturedContent = useCallback(async () => {
    try {
      setFeaturedLoading(true);
      const startTime = Date.now();
      
      const [featuredMoviesRes, featuredShowsRes] = await Promise.all([
        MoviesAPI.getFeatured(),
        ShowsAPI.getFeatured()
      ]);
      
      // Check if request took longer than expected
      const requestTime = Date.now() - startTime;
      if (requestTime > 2000) { // Reduced from 3000
        setShowHighTrafficMessage(true);
      }
      
      // Process featured content
      const allFeatured = [
        ...(featuredMoviesRes.data.data || []),
        ...(featuredShowsRes.data.data || [])
      ];
      
      // Randomize and take up to 5 items
      return allFeatured
        .sort(() => 0.5 - Math.random()) // Randomize the content
        .slice(0, 5); // Take only 5 items
    } catch (error) {
      setShowHighTrafficMessage(true);
      console.error('Error fetching featured content:', error);
      throw error;
    } finally {
      setFeaturedLoading(false);
    }
  }, [setShowHighTrafficMessage]);
  
  // Separate hook for featured content - loaded first for better UX
  useEffect(() => {
    let isMounted = true;
    
    const loadFeaturedContent = async () => {
      try {
        const featuredContent = await fetchFeaturedContent();
        
        if (!isMounted) return;
        
        setFeatured(featuredContent);
        
        // Start preloading featured images in the background
        // but don't wait for it to complete
        Promise.all(
          featuredContent
            .slice(0, 2) // Only preload first 2 images for faster initial load
            .map(item => preloadImage(item.coverImage || item.image))
        ).finally(() => {
          if (isMounted) {
            setFeaturedLoaded(true);
            // Set loading to false as soon as featured content is loaded
            // This will remove the initial loading screen faster
            setLoading(false);
          }
        });
      } catch (err) {
        console.error('Failed to load featured content:', err);
        if (isMounted) {
          setFeaturedLoaded(true);
          setLoading(false); // Allow the page to continue loading even if featured fails
        }
      }
    };
    
    loadFeaturedContent();
    
    return () => {
      isMounted = false;
    };
  }, [fetchFeaturedContent]);
  
  // Load trending content separately - after featured is loaded
  useEffect(() => {
    let isMounted = true;
    
    const fetchTrendingContent = async () => {
      if (!featuredLoaded) return;
      
      try {
        setTrendingLoading(true);
        const trendingRes = await PopularityAPI.getTrending(15);
        
        if (!isMounted) return;
        
        const trendingList = trendingRes.data.data || [];
        setTrending(trendingList);
      } catch (err) {
        console.error('Error fetching trending content:', err);
      } finally {
        if (isMounted) {
          setTrendingLoading(false);
        }
      }
    };
    
    // Add a small delay before loading other content
    const timer = setTimeout(() => {
      fetchTrendingContent();
    }, 100);
    
    return () => {
      clearTimeout(timer);
      isMounted = false;
    };
  }, [featuredLoaded]);
  
  // Load popular movies separately - after featured is loaded
  useEffect(() => {
    let isMounted = true;
    
    const fetchPopularMovies = async () => {
      if (!featuredLoaded) return;
      
      try {
        setMoviesLoading(true);
        const moviesRes = await PopularityAPI.getPopularMovies(15);
        
        if (!isMounted) return;
        
        const moviesList = moviesRes.data.data || [];
        setPopularMovies(moviesList);
      } catch (err) {
        console.error('Error fetching popular movies:', err);
      } finally {
        if (isMounted) {
          setMoviesLoading(false);
        }
      }
    };
    
    // Add a small delay before loading other content
    const timer = setTimeout(() => {
      fetchPopularMovies();
    }, 200);
    
    return () => {
      clearTimeout(timer);
      isMounted = false;
    };
  }, [featuredLoaded]);
  
  // Load popular shows separately - after featured is loaded
  useEffect(() => {
    let isMounted = true;
    
    const fetchPopularShows = async () => {
      if (!featuredLoaded) return;
      
      try {
        setShowsLoading(true);
        const showsRes = await PopularityAPI.getPopularShows(15);
        
        if (!isMounted) return;
        
        const showsList = showsRes.data.data || [];
        setPopularShows(showsList);
      } catch (err) {
        console.error('Error fetching popular shows:', err);
      } finally {
        if (isMounted) {
          setShowsLoading(false);
        }
      }
    };
    
    // Add a small delay before loading other content
    const timer = setTimeout(() => {
      fetchPopularShows();
    }, 300);
    
    return () => {
      clearTimeout(timer);
      isMounted = false;
    };
  }, [featuredLoaded]);
  
  // Load recently added content separately - after featured is loaded
  useEffect(() => {
    let isMounted = true;
    
    const fetchRecentlyAdded = async () => {
      if (!featuredLoaded) return;
      
      try {
        setRecentlyAddedLoading(true);
        const recentlyAddedRes = await UserAPI.getRecentlyAdded();
        
        if (!isMounted) return;
        
        // Set recently added content and ensure it's sorted by creation date (newest first)
        const sortedRecentlyAdded = (recentlyAddedRes.data.data || [])
          .sort((a, b) => {
            // Parse the UTC date strings and ensure proper timezone handling
            const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return dateB - dateA;
          });
        
        setRecentlyAdded(sortedRecentlyAdded);
      } catch (err) {
        console.error('Error fetching recently added content:', err);
      } finally {
        if (isMounted) {
          setRecentlyAddedLoading(false);
        }
      }
    };
    
    // Add a small delay before loading other content
    const timer = setTimeout(() => {
      fetchRecentlyAdded();
    }, 400);
    
    return () => {
      clearTimeout(timer);
      isMounted = false;
    };
  }, [featuredLoaded]);
  
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    setFeaturedLoaded(false);
    setFeaturedLoading(true);
    setTrendingLoading(true);
    setMoviesLoading(true);
    setShowsLoading(true);
    setRecentlyAddedLoading(true);
    window.location.reload();
  };

  const clearError = () => {
    setError(null);
  };
  
  // Show loading overlay while fetching initial data
  if (loading && !featuredLoaded) {
    return (
      <LoadingOverlay>
        <LoadingSpinner>
          <FontAwesomeIcon icon={faSpinner} />
          <p>Loading content...</p>
          {showHighTrafficMessage && (
            <HighTrafficMessage>
              <p>We're experiencing higher than usual traffic right now. Your content is being loaded, please wait a moment...</p>
            </HighTrafficMessage>
          )}
        </LoadingSpinner>
      </LoadingOverlay>
    );
  }
  
  // If there's an error, show it in a full-screen overlay
  if (error) {
    return (
      <ErrorOverlay>
        <ErrorWrapper>
          <ErrorNotification message={error} onClose={clearError} />
          <HighTrafficMessage>
            <p>We're experiencing higher than usual traffic right now. Please try again in a few moments.</p>
          </HighTrafficMessage>
          <RetryButton onClick={handleRetry}>
            <FontAwesomeIcon icon={faRedo} /> Retry Loading Content
          </RetryButton>
        </ErrorWrapper>
      </ErrorOverlay>
    );
  }
  
  // Main content render with Suspense boundaries for lazy-loaded components
  return (
    <ContentContainer>
      {/* Featured content - loaded directly without Suspense for faster initial render */}
      {featuredLoading ? (
        <FeaturedContainer>
          <SectionLoadingSpinner>
            <FontAwesomeIcon icon={faSpinner} />
            <p>Loading featured content...</p>
          </SectionLoadingSpinner>
        </FeaturedContainer>
      ) : featured.length > 0 && (
        <FeaturedContainer>
          <Featured items={featured} />
        </FeaturedContainer>
      )}
      
      <Suspense fallback={<SuspenseFallback></SuspenseFallback>}>
        {currentUser && continueWatching && continueWatching.length > 0 && (
          <ContentSlider 
            title="Continue Watching" 
            items={continueWatching}
            watchHistory={continueWatching} 
          />
        )}
      </Suspense>
      
      {/* Split section for trending and recently added */}
      <SplitSection>
        <Suspense fallback={<SuspenseFallback></SuspenseFallback>}>
          <TrendingSection>
            {trendingLoading ? (
              <SectionLoadingSpinner>
                <FontAwesomeIcon icon={faSpinner} />
                <p>Loading trending content...</p>
              </SectionLoadingSpinner>
            ) : trending.length > 0 && (
              <ContentSlider title="Trending Now" items={trending} />
            )}
          </TrendingSection>
        </Suspense>
        
        <Suspense fallback={<SuspenseFallback></SuspenseFallback>}>
          {recentlyAddedLoading ? (
            <SectionLoadingSpinner>
              <FontAwesomeIcon icon={faSpinner} />
              <p>Loading recent content...</p>
            </SectionLoadingSpinner>
          ) : (
            <RecentlyAdded items={recentlyAdded} />
          )}
        </Suspense>
      </SplitSection>
      
      <Suspense fallback={<SuspenseFallback></SuspenseFallback>}>
        {moviesLoading ? (
          <SectionLoadingSpinner>
            <FontAwesomeIcon icon={faSpinner} />
            <p>Loading popular movies...</p>
          </SectionLoadingSpinner>
        ) : popularMovies.length > 0 && (
          <ContentSlider title="Popular Movies" items={popularMovies} />
        )}
      </Suspense>
      
      <Suspense fallback={<SuspenseFallback></SuspenseFallback>}>
        {showsLoading ? (
          <SectionLoadingSpinner>
            <FontAwesomeIcon icon={faSpinner} />
            <p>Loading popular shows...</p>
          </SectionLoadingSpinner>
        ) : popularShows.length > 0 && (
          <ContentSlider title="Popular Shows" items={popularShows} />
        )}
      </Suspense>
      
      <Footer>
        <FooterContent>
          <FooterLinks>
            <FooterLink to="/privacy">Privacy Policy</FooterLink>
            <FooterLink to="/dmca">DMCA</FooterLink>
          </FooterLinks>
          <Disclaimer>
            Odelu.fun does not host any content on our servers. We function solely as a frontend interface to content that is hosted elsewhere on the internet. 
            For any inquiries or requests, please contact us at odelu0@proton.me
          </Disclaimer>
        </FooterContent>
      </Footer>
    </ContentContainer>
  );
};

export default HomePage;